import React from "react";
import { Loader2, Trash2, MessageSquareText } from "lucide-react"; // User icon not used from here
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { ChatPreview } from "@/components/messages/types";

interface ChatListProps {
  chats: ChatPreview[];
  isLoading: boolean;
  selectedChatId: number | null;
  onSelectChat: (chatId: number) => void;
  handleDeleteChat: (chatId: number) => void;
  formatChatDate: (dateString: string | undefined) => string;
  currentUserId: string;
}

const ChatList: React.FC<ChatListProps> = ({
  chats,
  isLoading,
  selectedChatId,
  onSelectChat,
  handleDeleteChat,
  formatChatDate,
  // currentUserId, // Not directly used in JSX
}) => {
  return (
    <div className="h-full flex flex-col"> {/* Ensure it's a flex column */}
      <div className="p-3 md:p-4 border-b border-gray-200 dark:border-gray-700"> {/* Adjusted padding */}
        <h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white">Mesazhet</h2>
      </div>

      <ScrollArea className="flex-grow" type="auto"> {/* flex-grow to take available space */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full p-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 w-full">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[150px]" />
                  <Skeleton className="h-3 w-[100px]" />
                </div>
              </div>
            ))}
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-12 px-4">
            <MessageSquareText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <p className="mt-4 text-gray-600 dark:text-gray-300">Asnjë mesazh</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Kur të filloni një bisedë të re, ajo do të shfaqet këtu.
            </p>
          </div>
        ) : (
          chats.map((chat) => (
            <div
              key={chat.id}
              className={`flex items-center p-2.5 md:p-3 border-b border-gray-100 dark:border-gray-750 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer transition-colors duration-150 group relative ${ // Added group relative for delete button positioning
                selectedChatId === chat.id ? "bg-slate-100 dark:bg-slate-700" : ""
              }`}
              onClick={() => onSelectChat(chat.id)}
            >
              <Avatar className="h-9 w-9 md:h-10 md:w-10 mr-2.5 md:mr-3"> {/* Adjusted margin */}
                <AvatarImage src={chat.other_user_profile_image || ''} alt={chat.other_user_name} />
                <AvatarFallback className="bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                  {(chat.other_user_name || "U").charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>

              <div className="flex-grow min-w-0">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-gray-800 dark:text-gray-100 truncate text-sm md:text-base"> {/* Adjusted size */}
                    {chat.other_user_name}
                  </p>
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                    {formatChatDate(chat.last_message_at || chat.updated_at)}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-0.5">
                  <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 truncate max-w-[calc(100%-30px)]"> {/* Adjusted size and max-width */}
                    {chat.last_message}
                  </p>
                  {chat.unread_count && chat.unread_count > 0 ? (
                    <span className="bg-brand-500 text-white text-[10px] md:text-xs font-semibold px-1.5 py-0.5 rounded-full ml-1 min-w-[18px] text-center"> {/* Adjusted unread count style */}
                      {chat.unread_count}
                    </span>
                  ) : null}
                </div>
                 {chat.listing_title && (
                   <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                     Re: {chat.listing_title}
                   </p>
                 )}
              </div>
              
              {/* Delete button positioned absolutely on hover for desktop, visible on mobile within flex flow */}
              <div className="ml-1 md:absolute md:right-2 md:top-1/2 md:-translate-y-1/2 md:opacity-0 group-hover:md:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 md:h-8 md:w-8 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-500" // Adjusted size
                  onClick={(e) => {
                      e.stopPropagation(); // Prevent chat selection
                      handleDeleteChat(chat.id);
                  }}
                  aria-label="Fshij bisedën"
                >
                  <Trash2 className="h-4 w-4 md:h-4.5 md:w-4.5" /> {/* Adjusted icon size */}
                </Button>
              </div>
            </div>
          ))
        )}
      </ScrollArea>
    </div>
  );
};

export default ChatList;
